import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMoon, FaSun, FaTimes, FaBars, FaHome, FaUtensils, FaInfoCircle, FaFire, FaEnvelope, FaCalendarAlt, FaHeart, FaShoppingCart, FaUser, FaEllipsisH } from 'react-icons/fa';
import { HashLink } from 'react-router-hash-link';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import HeaderTop from "../HeaderTop/HeaderTop.js";
import LanguageContext from '../../../context/LanguageContext.js';
import { usePublicSettings } from '../../../context/PublicSettingsContext.js';
import { useStorePathPrefix } from '../../../lib/storefront/storePath.js';
import { useStoreLogo } from '../../../hooks/useStoreLogo.js';

function Header() {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const storePrefix = useStorePathPrefix();
    const { src: logoSrc, alt: logoAlt, onError: onLogoError, isRemote } = useStoreLogo();
    const [theme, setTheme] = useState(localStorage.getItem('selected-theme') || 'light');
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();

    const getLangPrefix = () => `${storePrefix}/${currentLanguage}`;

    // #hero sits below the fixed header (body margin-top), so the default
    // scrollIntoView would land past the page top and tuck the hero under the header.
    const scrollToPageTop = () => window.scrollTo({ top: 0 });

    useEffect(() => {
        document.body.classList.toggle('dark-theme', theme === 'dark');
        localStorage.setItem('selected-theme', theme);
    }, [theme]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.pageYOffset;

            const sections = document.querySelectorAll('section[id]');
            sections.forEach((current) => {
                const sectionHeight = current.offsetHeight;
                const sectionTop = current.offsetTop - 58;
                const sectionId = current.getAttribute('id');
                if (!sectionId) return;
                let link = null;
                try {
                    link = document.querySelector(`.nav-menu a[href*="${CSS.escape(sectionId)}"]`);
                } catch {
                    link = null;
                }

                if (link) {
                    link.classList.toggle('active-link', scrollY > sectionTop && scrollY <= sectionTop + sectionHeight);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        // Lets floating widgets (support chat) step aside while the mobile menu is open.
        document.body.classList.toggle('mobile-menu-open', menuOpen);
        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('mobile-menu-open');
        };
    }, [menuOpen]);

    const handleThemeChange = () => {
        setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
    };

    const handleMenuToggle = () => {
        setMenuOpen((prevMenuOpen) => !prevMenuOpen);
    };

    const handleLinkClick = () => {
        setMenuOpen(false);
    };

    const isActive = (path) => location.pathname === path;
    const { publicSettings } = usePublicSettings();
    const isPageEnabled = (slug) => {
        if (!publicSettings?.storefrontPages || typeof publicSettings.storefrontPages !== 'object') return true;
        const entry = publicSettings.storefrontPages[slug];
        if (!entry || typeof entry !== 'object') return true;
        return entry.enabled !== false;
    };

    const showWishlistInHeader = isPageEnabled('wishlist') && (process.env.REACT_APP_SHOW_WISHLIST_HEADER || 'true').toLowerCase() === 'true';
    const showReservationInHeader = isPageEnabled('reservation') && (process.env.REACT_APP_SHOW_RESERVATION_HEADER || 'false').toLowerCase() === 'true';
    // Blog link is disabled below; re-enable this (and import FaNewspaper) together with it.
    // const showBlogInHeader = isPageEnabled('blog') && (process.env.REACT_APP_SHOW_BLOG_HEADER || 'false').toLowerCase() === 'true';
    const showAboutInHeader = isPageEnabled('about');
    const showContactInHeader = isPageEnabled('contact');

    return (
        <div className={`${theme}`}>
            <header className="header" id="header">

                <HeaderTop />
                
                <nav className="nav container">
                    
                    <Link to={getLangPrefix()} className="nav-logo" aria-label={logoAlt}>
                        <img
                            src={logoSrc}
                            className={`nav-logo-icon${isRemote ? '' : ' nav-logo-icon--brand'}`}
                            alt={logoAlt}
                            onError={onLogoError}
                            decoding="async"
                        />
                    </Link>
                    
                    <div className={`nav-backdrop ${menuOpen ? 'show' : ''}`} onClick={handleMenuToggle}></div>
                    <div className={`nav-menu ${menuOpen ? 'show-menu' : ''}`} id="nav-menu">
                        <div className="nav-menu-handle"></div>
                        <ul className="nav-list" dir={dir()}>
                            <li className="nav-item">
                                <HashLink
                                    to={`${getLangPrefix()}#hero`}
                                    scroll={scrollToPageTop}
                                    className={`nav-link ${location.hash === '#hero' && location.pathname === getLangPrefix() ? 'active-link' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    <FaHome className="nav-link-icon" />
                                    {t('nav.home')}
                                </HashLink>
                            </li>
                            <li className="nav-item">
                                <HashLink
                                    to={`${getLangPrefix()}/menu`}
                                    className={`nav-link ${isActive(`${getLangPrefix()}/menu`) ? 'active-link' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    <FaUtensils className="nav-link-icon" />
                                    {t('nav.menu')}
                                </HashLink>
                            </li>
                            {showAboutInHeader && (
                                <li className="nav-item">
                                    <HashLink
                                        to={`${getLangPrefix()}/about`}
                                        className={`nav-link ${isActive(`${getLangPrefix()}/about`) ? 'active-link' : ''}`}
                                        onClick={handleLinkClick}
                                    >
                                        <FaInfoCircle className="nav-link-icon" />
                                        {t('nav.about')}
                                    </HashLink>
                                </li>
                            )}
                            <li className="nav-item">
                                <HashLink
                                    to={`${getLangPrefix()}#popular-dishes`}
                                    className={`nav-link ${location.hash === '#popular-dishes' && location.pathname === getLangPrefix() ? 'active-link' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    <FaFire className="nav-link-icon" />
                                    {t('nav.popularDishes')}
                                </HashLink>
                            </li>
                            {showContactInHeader && (
                                <li className="nav-item">
                                    <HashLink
                                        to={`${getLangPrefix()}/contact`}
                                        className={`nav-link ${isActive(`${getLangPrefix()}/contact`) ? 'active-link' : ''}`}
                                        onClick={handleLinkClick}
                                    >
                                        <FaEnvelope className="nav-link-icon" />
                                        {t('nav.contact')}
                                    </HashLink>
                                </li>
                            )}
                            {showReservationInHeader && (
                                <li className="nav-item">
                                    <HashLink
                                        to={`${getLangPrefix()}/reservation`}
                                        className={`nav-link ${isActive(`${getLangPrefix()}/reservation`) ? 'active-link' : ''}`}
                                        onClick={handleLinkClick}
                                    >
                                        <FaCalendarAlt className="nav-link-icon" />
                                        {t('nav.reservation')}
                                    </HashLink>
                                </li>
                            )}
                            {/*{showBlogInHeader && (*/}
                            {/*    <li className="nav-item">*/}
                            {/*        <HashLink*/}
                            {/*            to={`${getLangPrefix()}/blog`}*/}
                            {/*            className={`nav-link ${isActive(`${getLangPrefix()}/blog`) || location.pathname.startsWith(`${getLangPrefix()}/blog/`) ? 'active-link' : ''}`}*/}
                            {/*            onClick={handleLinkClick}*/}
                            {/*        >*/}
                            {/*            <FaNewspaper className="nav-link-icon" />*/}
                            {/*            {t('nav.blog')}*/}
                            {/*        </HashLink>*/}
                            {/*    </li>*/}
                            {/*)}*/}
                            {showWishlistInHeader && (
                                <li className="nav-item">
                                    <HashLink
                                        to={`${getLangPrefix()}/wishlist`}
                                        className={`nav-link ${isActive(`${getLangPrefix()}/wishlist`) ? 'active-nav-link' : ''}`}
                                        onClick={handleLinkClick}
                                    >
                                        <FaHeart className="nav-link-icon--always" />
                                        <span className="nav-link-text">{t('nav.wishlist')}</span>
                                    </HashLink>
                                </li>
                            )}
                            <li className="nav-item">
                                <HashLink
                                    to={`${getLangPrefix()}/cart`}
                                    className={`nav-link ${isActive(`${getLangPrefix()}/cart`) ? 'active-nav-link' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    <FaShoppingCart className="nav-link-icon--always" />
                                    <span className="nav-link-text">{t('nav.cart')}</span>
                                </HashLink>
                            </li>
                            <li className="nav-item">
                                <Link
                                    to={`${getLangPrefix()}/sign-in`}
                                    className={`nav-link ${isActive(`${getLangPrefix()}/sign-in`) ? 'active-nav-link' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    <FaUser className="nav-link-icon--always" />
                                    <span className="nav-link-text">{t('nav.signIn', 'Sign In')}</span>
                                </Link>
                            </li>
                            <li className="nav-item nav-item--theme-desktop">
                                <button
                                    type="button"
                                    className="nav-link change-theme change-theme--nav-desktop"
                                    onClick={handleThemeChange}
                                    aria-label={t('header.toggleTheme')}
                                >
                                    {theme === 'light' ? <FaMoon /> : <FaSun />}
                                </button>
                            </li>
                        </ul>
                        <div className="nav-close" onClick={handleMenuToggle}>
                            <FaTimes />
                        </div>
                    </div>
                    <div className="nav-btns">
                        <button className="change-theme" onClick={handleThemeChange} aria-label={t('header.toggleTheme')}>
                            {theme === 'light' ? <FaMoon /> : <FaSun />}
                        </button>
                        <Link to={`${getLangPrefix()}/sign-in`} className="nav-user-mobile" onClick={handleLinkClick} aria-label={t('nav.signIn', 'Sign In')}>
                            <FaUser />
                        </Link>
                        <div className="nav-toggle" onClick={handleMenuToggle} aria-label={t('nav.toggleMenu', 'Toggle Menu')}>
                            <FaBars />
                        </div>
                    </div>
                </nav>
            </header>

            <nav className="nav-bottom-bar" dir={dir()}>
                <HashLink
                    to={`${getLangPrefix()}#hero`}
                    scroll={scrollToPageTop}
                    className={`bottom-bar-item ${location.hash === '#hero' && location.pathname === getLangPrefix() ? 'bottom-bar-item--active' : ''}`}
                    onClick={handleLinkClick}
                >
                    <FaHome className="bottom-bar-icon" />
                    <span className="bottom-bar-label">{t('nav.home')}</span>
                </HashLink>
                <HashLink
                    to={`${getLangPrefix()}/menu`}
                    className={`bottom-bar-item ${isActive(`${getLangPrefix()}/menu`) ? 'bottom-bar-item--active' : ''}`}
                    onClick={handleLinkClick}
                >
                    <FaUtensils className="bottom-bar-icon" />
                    <span className="bottom-bar-label">{t('nav.menu')}</span>
                </HashLink>
                {showWishlistInHeader && (
                    <HashLink
                        to={`${getLangPrefix()}/wishlist`}
                        className={`bottom-bar-item ${isActive(`${getLangPrefix()}/wishlist`) ? 'bottom-bar-item--active' : ''}`}
                        onClick={handleLinkClick}
                    >
                        <FaHeart className="bottom-bar-icon" />
                        <span className="bottom-bar-label">{t('nav.wishlist')}</span>
                    </HashLink>
                )}
                <HashLink
                    to={`${getLangPrefix()}/cart`}
                    className={`bottom-bar-item ${isActive(`${getLangPrefix()}/cart`) ? 'bottom-bar-item--active' : ''}`}
                    onClick={handleLinkClick}
                >
                    <FaShoppingCart className="bottom-bar-icon" />
                    <span className="bottom-bar-label">{t('nav.cart')}</span>
                </HashLink>
                <button
                    className={`bottom-bar-item ${menuOpen ? 'bottom-bar-item--active' : ''}`}
                    onClick={handleMenuToggle}
                    aria-label={t('nav.moreMenu', 'More menu')}
                >
                    <FaEllipsisH className="bottom-bar-icon" />
                    <span className="bottom-bar-label">{t('nav.more', 'More')}</span>
                </button>
            </nav>
        </div>
    );
}

export default Header;
